import { Brain, Eye, Shield, Lightbulb, Activity, Crosshair } from "lucide-react";

export const P = {
    navy: "#1E3A5F", blue: "#4A7AB5", blueLight: "#4A7AB5",
    green: "#0D9373", greenBg: "#ECFDF5", greenText: "#065F46",
    amber: "#D97706", amberBg: "#FFFBEB", amberText: "#92400E",
    red: "#DC2626", redBg: "#FEF2F2", redText: "#991B1B",
    slate: "#475569", light: "#F8FAFC", border: "#E2E8F0",
};

export const DIM_COLORS = ["#3B82F6", "#F59E0B", "#EF4444", "#06B6D4", "#22C55E", "#A855F7"];
export const DIM_ICONS = [Brain, Eye, Shield, Lightbulb, Activity, Crosshair];

export function getScoreColor(s) { return s >= 75 ? P.green : s >= 50 ? P.blue : s >= 35 ? P.amber : P.red; }
export function getScoreBg(s) { return s >= 75 ? P.greenBg : s >= 50 ? "#EFF6FF" : s >= 35 ? P.amberBg : P.redBg; }
export function getScoreLabel(s) { return s >= 75 ? "Strong" : s >= 50 ? "Moderate" : s >= 35 ? "Developing" : "Low"; }

export function deriveInsights(dimScores) {
    const sorted = Object.entries(dimScores).sort((a, b) => b[1].normalized - a[1].normalized);
    const strengths = sorted.slice(0, 2).map(([, d]) => d);
    const devAreas = sorted.slice(-2).map(([, d]) => d);
    return { strengths, devAreas };
}

export function generateInterpretation(total, archetype, aps, defense) {
    const level = total >= 75 ? "high" : total >= 50 ? "moderate" : "emerging";
    const action = aps?.score >= 70 ? "strong action orientation" : aps?.score >= 40 ? "moderate action potential" : "limited follow-through tendency";
    const def = defense?.count <= 3 ? "low resistance to feedback" : defense?.count <= 6 ? "some situational defensiveness" : "elevated defensive patterns";
    if (level === "high") return `This individual demonstrates high coachability with ${action} and ${def}. Coaching investment is likely to yield significant behavioral change.`;
    if (level === "moderate") return `This individual shows ${level} coachability with ${action} and ${def}. Targeted coaching can unlock meaningful growth with the right approach.`;
    return `This individual presents emerging coachability with ${action} and ${def}. A structured, patience-oriented coaching strategy is recommended.`;
}

export function ScoreArc({ score, size = 140, label, sub }) {
    const pct = Math.min(score / 100, 1);
    const r = (size - 16) / 2;
    const halfCirc = Math.PI * r;
    const offset = halfCirc * (1 - pct);
    const color = getScoreColor(score);
    return (
        <div className="flex flex-col items-center">
            <svg width={size} height={size * 0.62} viewBox={`0 0 ${size} ${size * 0.62}`}>
                <path d={`M 8 ${size * 0.58} A ${r} ${r} 0 0 1 ${size - 8} ${size * 0.58}`}
                    fill="none" stroke="#E2E8F0" strokeWidth="10" strokeLinecap="round" />
                <path d={`M 8 ${size * 0.58} A ${r} ${r} 0 0 1 ${size - 8} ${size * 0.58}`}
                    fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
                    strokeDasharray={halfCirc} strokeDashoffset={offset}
                    style={{ transition: "stroke-dashoffset 1.2s ease" }} />
                <text x="50%" y={size * 0.5} textAnchor="middle" fill="#1E293B" fontSize="26" fontWeight="700" fontFamily="Outfit">
                    {score.toFixed(1)}
                </text>
            </svg>
            {label && <p className="text-xs font-semibold text-slate-700 mt-1" style={{ fontFamily: "Outfit" }}>{label}</p>}
            {sub && <p className="text-[0.65rem] text-slate-400 mt-0.5">{sub}</p>}
        </div>
    );
}

export function SectionTitle({ icon: Icon, title, subtitle, id }) {
    return (
        <div id={id} className="flex items-center gap-3 pt-10 pb-5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: P.navy }}>
                <Icon className="h-4.5 w-4.5 text-white" style={{ width: 18, height: 18 }} />
            </div>
            <div>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{title}</h2>
                {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
            </div>
            <div className="flex-1 h-px bg-slate-100 ml-4" />
        </div>
    );
}

export function KPICard({ icon: Icon, value, label, sub, color, bg }) {
    return (
        <div className="rounded-xl border border-slate-100 bg-white p-5 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: bg || "#EFF6FF" }}>
                <Icon className="h-5 w-5" style={{ color: color || P.blue }} />
            </div>
            <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{value}</p>
            <p className="text-[0.7rem] font-semibold text-slate-500 uppercase tracking-wider mt-1">{label}</p>
            {sub && <p className="text-[0.65rem] text-slate-400 mt-0.5">{sub}</p>}
        </div>
    );
}

export function DimBar({ dim, color, icon: Icon }) {
    const score = dim.normalized;
    const bg = getScoreBg(score);
    const label = getScoreLabel(score);
    return (
        <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-white hover:shadow-sm transition-all">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + "15" }}>
                <Icon className="h-4 w-4" style={{ color }} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1.5">
                    <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: "Outfit" }}>{dim.short_name}</p>
                    <div className="flex items-center gap-2">
                        <span className="text-[0.65rem] font-bold px-2 py-0.5 rounded-full" style={{ background: bg, color: getScoreColor(score) }}>{label}</span>
                        <span className="text-sm font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{score.toFixed(0)}%</span>
                    </div>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all duration-1000" style={{ width: `${score}%`, background: color }} />
                </div>
            </div>
        </div>
    );
}
